<script setup>
import Wizard from "@/components/wizard/Wizard.vue";
import Modal from "@/components/modal/Modal.vue";
import {useWizard} from "@/components/wizard";
import {ref, useTemplateRef} from "vue";
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {pickTriageValues} from "@/components/hpo/triage/schema";
import WizardStep from "@/components/wizard/WizardStep.vue";
import TicScreen from "@/components/hpo/triage/TicScreen.vue";
import IsChildScreen from "@/components/hpo/triage/IsChildScreen.vue";
import GenderScreen from "@/components/hpo/triage/GenderScreen.vue";
import PriorityScreen from "@/components/hpo/triage/PriorityScreen.vue";
import DiagnosisScreen from "@/components/hpo/transport/DiagnosisScreen.vue";
import {pickTransportValues, transportSchema} from "@/components/hpo/transport/schema";
import VentilatedScreen from "@/components/hpo/transport/VentilatedScreen.vue";
import UnitScreen from "@/components/hpo/transport/UnitScreen.vue";
import HospitalTypeScreen from "@/components/hpo/transport/HospitalTypeScreen.vue";
import TransportSummaryScreen from "@/components/hpo/transport/TransportSummaryScreen.vue";
import {useMutation} from "@vue/apollo-composable";
import {HpoPatientTransportMutation} from "@/graphql/mutations/hpo/patient/transport.graphql";
import {HpoTransportStatus} from "@/components/hpo/choices";
import {mdiCheckCircle, mdiChevronLeft, mdiClose, mdiContentSaveCheck, mdiDeleteCircle} from "@mdi/js";
import TransportWizardProgress from "@/components/hpo/transport/TransportWizardProgress.vue";
import {useWizardLeaveConfirmation} from "@/components/wizard/composition";
import {pgettext} from "@/utils/translate";
import FormErrorDialog from "@/components/hpo/FormErrorDialog.vue";

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  patientList: {
    type: Array,
    default: () => [],
  },
  patientData: {
    type: Object,
  },
});
const emit = defineEmits(['close']);

const errorDialog = useTemplateRef('errorDialog');

function close() {
  emit('close');
}

const launchedAsEdit = !!props.patientData;
const isEditing = ref(launchedAsEdit);

let startStepName = 'tic';
if (launchedAsEdit) {
  if (props.patientData.transportStatus) {
    startStepName = 'summary';
  } else {
    startStepName = 'transportPriority';
  }
}

const wizard = useWizard({
  startStepName,
});

const ticStep = wizard.defineStep('tic', {
  next({tic}) {
    for (const patient of props.patientList) {
      if (patient.tic === tic) {
        patientId.value = patient?.id;
        isEditing.value = true;
        let values = pickTriageValues(patient);

        let targetStep = 'transportPriority';
        if (patient.transportStatus) {
          existingTransportStatus.value = patient.transportStatus;
          Object.assign(values, pickTransportValues(patient));
          targetStep = 'summary';
        }
        resetForm({values});
        return targetStep;
      }
    }
  },
  enter() {
    if (!launchedAsEdit && patientId.value) {
      patientId.value = null;
      isEditing.value = false;
      existingTransportStatus.value = null;
      resetForm({
        values: {
          ticPrefix: ticPrefix.value,
          ticNumber: ticNumber.value,
        },
      });
    }
  },
});
const transportPriorityStep = wizard.defineStep('transportPriority');
const genderStep = wizard.defineStep('gender');
const isChildStep = wizard.defineStep('isChild');
const diagnoseStep = wizard.defineStep('diagnose');
const ventilatedStep = wizard.defineStep('ventilated');
const unitStep = wizard.defineStep('unit');
const hospitalTypeStep = wizard.defineStep('hospitalType');
const summaryStep = wizard.defineStep('summary');
const confirmationStep = wizard.defineStep('confirmation', {
  enableBack: false,
});
wizard.onEnd(() => {
  close();
});

function editStep(stepName) {
  wizard.pushFlow([stepName]);
}

const schema = yup.object({
  ...transportSchema,
});

const {
  defineField,
  handleMutationSubmit,
  values,
  isSubmitting,
  resetForm,
  meta: formMeta,
  validateField,
  errors,
} = useDjangoForm({
  validationSchema: schema,
  initialValues: {
    ...pickTransportValues(props.patientData),
  },
});

const patientId = ref(props.patientData?.id || null);
const existingTransportStatus = ref(props.patientData?.transportStatus || null);
const [ticPrefix, ticPrefixProps] = defineField('ticPrefix');
const [ticNumber, ticNumberProps] = defineField('ticNumber');
const [gender, genderProps] = defineField('gender');
const [isChild, isChildProps] = defineField('isChild');

const [diagnose, diagnoseProps] = defineField('diagnose');
const [ventilated, ventilatedProps] = defineField('ventilated');
const [unitType, unitTypeProps] = defineField('unitType');
const [unitTypeSpec, unitTypeSpecProps] = defineField('unitTypeSpec');
const [callSign, callSignProps] = defineField('callSign');
const [transportPriority, transportPriorityProps] = defineField('transportPriority');
const [hospitalType, hospitalTypeProps] = defineField('hospitalType');


const {mutate} = useMutation(HpoPatientTransportMutation);

const desiredStatus = ref(HpoTransportStatus.DISP_PENDING);
const onSubmit = handleMutationSubmit(
  async (data) => {
    return mutate({
      id: patientId.value,
      transportStatus: desiredStatus.value,
      hpo: props.hpoId,
      ...data
    });
  },
  {
    onSuccess(result) {
      wizard.goNext();
    },
    onMutationError(error) {
      errorDialog.value.showMutationError(error);
    },
    onInvalidFormResponse(error) {
      errorDialog.value.showValidationError(error);
    },
  }
);

function handleSave({status, data}) {
  desiredStatus.value = status;
  onSubmit(data);
}

const fieldDefinitions = {
  ticPrefix: {label: pgettext('label', 'TIC Card Letter'), step: ticStep.name},
  ticNumber: {label: pgettext('label', 'TIC'), step: ticStep.name},
  gender: {label: pgettext('label', 'Gender'), step: genderStep.name},
  isChild: {label: pgettext('label', 'Adult or child?'), step: isChildStep.name},
  transportPriority: {label: pgettext('label', 'Transport Priority'), step: transportPriorityStep.name},
  diagnose: {label: pgettext('label', 'Diagnose'), step: diagnoseStep.name},
  ventilated: {label: pgettext('label', 'ALS'), step: ventilatedStep.name},
  unitType: {label: pgettext('label', 'Unit Type'), step: unitStep.name},
  unitTypeSpec: {label: pgettext('label', 'Unit Type'), step: unitStep.name},
  callSign: {label: pgettext('label', 'Call sing / LP'), step: unitStep.name},
  hospitalType: {label: pgettext('label', 'Hospital Type'), step: hospitalTypeStep.name},
};

useWizardLeaveConfirmation(
  wizard,
  formMeta,
  pgettext('confirm', 'You have an unsaved transport. Are you sure you want to leave this page?'),
  [
    confirmationStep,
  ],
);
</script>

<template>
  <Modal fullscreen @close="close">
    <template #header>
      <v-card-item>
        <div class="wizard__progress-header">
          <div>
            <v-btn @click="wizard.goBack()"
                   :prepend-icon="mdiChevronLeft"
                   :disabled="!wizard.canGoBack()">
              {{ pgettext('action', 'Back') }}
            </v-btn>
          </div>
          <div class="wizard__progress-bar text-center">
            <TransportWizardProgress v-if="wizard.currentStep.value.name !== confirmationStep.name"
                                     :formData="values"
                                     :wizard="wizard"/>
          </div>
          <div class="text-right">
            <v-btn variant="elevated" rounded="xl" size="x-large" :icon="mdiClose"
                   @click="close"></v-btn>
          </div>
        </div>
      </v-card-item>
    </template>
    <Wizard :wizard="wizard" :max-width="1300">
      <WizardStep :step="ticStep">
        <TicScreen v-model:ticPrefix="ticPrefix"
                   v-model:ticNumber="ticNumber"
                   :hpoId="hpoId"
                   :patientList="patientList"
                   :patientId="patientId"
                   @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="transportPriorityStep">
        <PriorityScreen v-model="transportPriority"
                        :title="pgettext('label', 'Transport Priority')"
                        :fieldProps="transportPriorityProps"
                        @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="genderStep">
        <GenderScreen v-model="gender" :fieldProps="genderProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="isChildStep">
        <IsChildScreen v-model="isChild" :fieldProps="isChildProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="diagnoseStep">
        <DiagnosisScreen v-model="diagnose" :fieldProps="diagnoseProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="ventilatedStep">
        <VentilatedScreen v-model="ventilated" :fieldProps="ventilatedProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="unitStep">
        <UnitScreen v-model:unitType="unitType"
                    :unitTypeProps="unitTypeProps"
                    v-model:unitTypeSpec="unitTypeSpec"
                    :unitTypeSpecProps="unitTypeSpecProps"
                    v-model:callSign="callSign"
                    :callSignProps="callSignProps"
                    :validateField="validateField"
                    @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="hospitalTypeStep">
        <HospitalTypeScreen v-model="hospitalType" :fieldProps="hospitalTypeProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="summaryStep">
        <TransportSummaryScreen :isEditing="isEditing"
                                :patientId="patientId"
                                :formData="values"
                                :existingTransportStatus="existingTransportStatus"
                                :isSubmitting="isSubmitting"
                                @pushStep="editStep" @submit="handleSave"/>
      </WizardStep>

      <WizardStep :step="confirmationStep">
        <v-empty-state v-if="desiredStatus === HpoTransportStatus.DRAFT" :icon="mdiContentSaveCheck"
                       color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Transport for patient %(tic)s successfully saved as draft.'), {tic: `${ticPrefix}${ticNumber}`})
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
        <v-empty-state v-else-if="desiredStatus === HpoTransportStatus.TERMINATED" :icon="mdiDeleteCircle"
                       color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Transport for patient %(tic)s successfully canceled.'), {tic: `${ticPrefix}${ticNumber}`})
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
        <v-empty-state v-else :icon="mdiCheckCircle" color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Transport for patient %(tic)s successfully saved.'), {tic: `${ticPrefix}${ticNumber}`})
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
      </WizardStep>
    </Wizard>

    <FormErrorDialog ref="errorDialog"
                     :title="pgettext('title', 'Error while saving transport')"
                     :fields="fieldDefinitions"
                     @visitStepsWithErrors="wizard.pushFlow"/>
  </Modal>
</template>
